import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui';
import { ThemeContext } from '../../contexts/ThemeContext';
import { useQueryClient } from '@tanstack/react-query';
import { storeKeys } from '../../hooks/api/useStore';
import { useAuth } from '@/contexts/AuthWrapper';

/**
 * Icon types supported by the sidebar
 */
type IconType =
  | 'home'
  | 'app'
  | 'tools'
  | 'models'
  | 'history'
  | 'supply-gpu'
  | 'rest-api'
  | 'help'
  | 'pricing'
  | 'none';

/**
 * Icon component props interface
 */
interface IconProps {
  /** Type of icon to display */
  iconType: IconType;
  /** Whether the icon is in active state */
  isActive?: boolean;
}

const Icon: React.FC<IconProps> = ({ iconType, isActive = false }) => {
  const getIconPath = () => {
    switch (iconType) {
      case 'home':
        return '/icons/home.svg';
      case 'app':
        return '/icons/AI.svg';
      case 'models':
        return '/icons/models.svg';
      case 'history':
        return '/icons/history.svg';
      case 'tools':
        return '/icons/tools.svg';
      case 'none':
        return null;
      case 'supply-gpu':
        return '/icons/supply_gpu.svg';
      case 'rest-api':
        return '/icons/rest_api.svg';
      case 'help':
        return '/icons/help.svg';
      case 'pricing':
        return '/icons/pricing.svg';
      default:
        return null; // For items without icons, we'll show empty space
    }
  };

  const iconPath = getIconPath();

  if (!iconPath) {
    return <div style={{ width: 22, height: 22 }} />; // Empty space for items without icons
  }

  return (
    <img
      src={iconPath}
      alt=""
      style={{
        width: 22,
        height: 22,
        filter: isActive
          ? 'brightness(0) saturate(100%) invert(35%) sepia(77%) saturate(1610%) hue-rotate(226deg) brightness(99%) contrast(104%)' // purple tint
          : 'none',
      }}
    />
  );
};

const ChevronIcon: React.FC<{ isOpen?: boolean }> = ({ isOpen = false }) => (
  <div
    style={{
      width: 20,
      height: 20,
      position: 'relative',
      boxShadow: '0px 3px 3px -1.5px rgba(0, 0, 0, 0.04)',
    }}
  >
    <img
      src="/icons/dropdown.svg"
      alt=""
      style={{
        width: 20,
        height: 20,
        transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)', // Point up when open, down when closed
        transition: 'transform 0.1s ease',
      }}
    />
  </div>
);

/**
 * Sidebar item component props interface
 */
interface SidebarItemProps {
  /** Type of icon to display */
  iconType: IconType;
  /** Text label for the item */
  text: string;
  /** Whether the item is currently active */
  isActive?: boolean;
  /** Whether the item has a submenu */
  hasSubmenu?: boolean;
  /** Function to call when item is clicked */
  onClick?: () => void;
  /** Whether the sidebar is in collapsed state */
  isCollapsed?: boolean;
  /** Whether to show the icon */
  showIcon?: boolean;
  /** Indentation level for nested items */
  indent?: number;
  /** Whether submenu is open (for parent items) */
  isOpen?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  iconType,
  text,
  isActive = false,
  hasSubmenu = false,
  onClick,
  isCollapsed = false,
  showIcon = true,
  indent = 0,
  isOpen,
}) => {
  const [hovered, setHovered] = useState(false);

  if (isCollapsed) {
    return (
      <div
        className="sidebar-item-collapsed"
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          background: isActive
            ? 'var(--surface-surface_3, #EBECF0)'
            : 'transparent',
          boxSizing: 'border-box',
        }}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {showIcon && <Icon iconType={iconType} isActive={isActive} />}
      </div>
    );
  }

  return (
    <div
      className="sidebar-item"
      style={{
        width: '100%',
        height: 40,
        paddingLeft: 12 + indent * 16, // Indent based on level
        paddingRight: 12,
        paddingTop: 8,
        paddingBottom: 8,
        background: isActive
          ? 'var(--surface-surface_3, #EBECF0)'
          : hovered
            ? 'var(--surface-surface_2, #F4F4F6)'
            : 'transparent',
        boxShadow: isActive
          ? '0px -1px 3px -2px rgba(0, 0, 0, 0.04) inset'
          : 'none',
        borderRadius: 10,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showIcon && <Icon iconType={iconType} isActive={isActive} />}
      <div
        style={{
          flex: 1,
          color: isActive
            ? 'var(--text-high_em, #0A0C11)'
            : 'var(--text-med_em, #5B616D)',
          fontSize: 13,
          fontFamily: 'Inter',
          fontWeight: isActive ? 600 : 500,
          lineHeight: '24px',
          wordWrap: 'break-word',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: '100%',
          transition: 'max-width 0.3s ease, opacity 0.3s ease',
        }}
      >
        {text}
      </div>
      {hasSubmenu && (
        <div
          style={{
            flexShrink: 0,
            transition: 'opacity 0.3s ease, transform 0.3s ease',
          }}
        >
          <ChevronIcon isOpen={isOpen || false} />
        </div>
      )}
    </div>
  );
};

const ThemeSwitcher: React.FC = () => {
  const { isDark, setIsDark } = useContext(ThemeContext);

  return (
    <div
      style={{
        width: '100%',
        height: 44,
        padding: 6,
        paddingLeft: 14,
        background: 'var(--surface-surface_3, #EBECF0)',
        borderRadius: 1000,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          minWidth: 0,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          marginRight: 8,
        }}
      >
        <div
          style={{
            color: 'var(--text-med_em, #5B616D)',
            fontSize: 13,
            fontFamily: 'Inter',
            fontWeight: 500,
            lineHeight: '24px',
            wordWrap: 'break-word',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            transition: 'max-width 0.3s ease, opacity 0.3s ease',
          }}
        >
          Theme
        </div>
      </div>
      <div
        style={{
          padding: 3,
          background: 'var(--surface-surface_2, #F4F4F6)',
          borderRadius: 1000,
          outline: '1px var(--outline-base_em, #F4F4F6) solid',
          outlineOffset: '-1px',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 6,
          display: 'flex',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={() => setIsDark(!isDark)}
      >
        {/* System (light) */}
        <button
          className="themeButton"
          style={{
            height: 28,
            padding: 6,
            background: !isDark
              ? 'var(--surface-surface_0, white)'
              : 'transparent',
            borderRadius: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            outline: 'none',
          }}
          onClick={() => setIsDark(false)}
        >
          <img
            src="/icons/switch_theme.svg"
            alt="System"
            style={{
              width: 15,
              height: 15,
              filter: isDark ? 'brightness(0) invert(1)' : 'none', // Makes icon white in dark mode
            }}
          />
        </button>
        {/* Dark */}
        <button
          className="themeButton"
          style={{
            height: 28,
            padding: 6,
            background: isDark
              ? 'var(--surface-surface_0, white)'
              : 'transparent',
            borderRadius: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            outline: 'none',
          }}
          onClick={() => setIsDark(true)}
        >
          <img
            src="/icons/moon.svg"
            alt="Dark"
            style={{ width: 15, height: 15 }}
          />
        </button>
      </div>
    </div>
  );
};

/**
 * Navigation item interface
 */
interface NavigationItem {
  id: string;
  text: string;
  iconType: IconType;
  hasSubmenu: boolean;
  path?: string;
  children?: Array<{ id: string; text: string; path?: string }>;
}

/**
 * Sidebar component props interface
 */
export interface SidebarProps {
  /** Whether the sidebar is visible */
  isVisible: boolean;
  /** Whether the sidebar is collapsed */
  isCollapsed?: boolean;
  /** Function to call when sidebar toggle is requested */
  onToggle: () => void;
  /** Mobile: whether viewport is mobile (burger menu mode) */
  isMobile?: boolean;
  /** Mobile: whether the mobile drawer is open */
  isMobileMenuOpen?: boolean;
  /** Mobile: close the mobile drawer */
  onCloseMobileMenu?: () => void;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Sidebar component following design system standards
 * Provides navigation and application controls
 *
 * @example
 * ```tsx
 * <Sidebar
 *   isVisible={sidebarVisible}
 *   onToggle={handleToggle}
 * />
 * ```
 */
const Sidebar: React.FC<SidebarProps> = ({
  isVisible,
  isCollapsed = false,
  onToggle,
  isMobile = false,
  isMobileMenuOpen = false,
  onCloseMobileMenu,
  className,
  style,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { isDark } = useContext(ThemeContext);
  const { isAuthenticated } = useAuth();

  const baseNavigationItems: NavigationItem[] = [
    {
      id: 'App',
      text: 'App',
      iconType: 'app',
      hasSubmenu: true,
      children: [
        { id: 'App/Text', text: 'Text', path: '/text-chat' },
        { id: 'App/TTS', text: 'TTS', path: '/tts' },
        { id: 'App/Image', text: 'Image', path: '/image-generation' },
      ],
    },
    {
      id: 'Tools',
      text: 'Tools',
      iconType: 'tools',
      hasSubmenu: false,
      path: '/tools',
    },
    {
      id: 'Models',
      text: 'Models',
      iconType: 'models',
      hasSubmenu: false, // Commented out dropdown functionality
      path: '/models', // Direct to models page (same as gallery)
      // children: [ // Commented out dropdown items
      //   { id: 'Models/Gallery', text: 'Gallery', path: '/models' },
      //   { id: 'Models/Installed', text: 'Installed' },
      // ],
    },
    {
      id: 'History',
      text: 'History',
      iconType: 'history',
      hasSubmenu: false,
      path: '/history',
    },
  ];

  const baseCtaItems: NavigationItem[] = [
    {
      id: 'supply-gpu',
      text: 'Supply GPU',
      iconType: 'supply-gpu',
      hasSubmenu: false,
      path: '/supply-gpu',
    },
    {
      id: 'rest-api',
      text: 'Rest API',
      iconType: 'rest-api',
      hasSubmenu: false,
      path: '/api',
    },
    {
      id: 'help',
      text: 'Help',
      iconType: 'help',
      hasSubmenu: false,
      path: '/help',
    },
    {
      id: 'pricing',
      text: 'Pricing',
      iconType: 'pricing',
      hasSubmenu: false,
      path: '/pricing',
    },
  ];

  const navigationItems: NavigationItem[] = baseNavigationItems.filter(item => {
    if (!isAuthenticated && (item.id === 'App' || item.id === 'Tools' || item.id === 'History')) {
      return false;
    }
    return true;
  });

  const ctaItems: NavigationItem[] = baseCtaItems;

  const toggleSubmenu = (id: string) => {
    setOpenSubmenus(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Determine active item based on current location
  const getActiveItem = () => {
    const currentPath = location.pathname;

    // Check navigation items
    for (const item of navigationItems) {
      if (item.path === currentPath) {
        return item.id;
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.path === currentPath) {
            return child.id;
          }
        }
      }
    }

    // Check CTA items
    for (const item of ctaItems) {
      if (item.path === currentPath) {
        return item.id;
      }
    }

    return ''; // Default fallback when no explicit item matches
  };

  const activeItem = getActiveItem();

  const handleItemClick = (item: NavigationItem) => {
    if (item.hasSubmenu) {
      toggleSubmenu(item.id);
    } else if (item.id === 'rest-api') {
      const themeParam = isDark ? 'dark' : 'light';
      window.open(
        `https://api.swarmind.ai/docs?theme=${themeParam}#/`,
        '_blank',
        'noopener,noreferrer'
      );
      if (isMobile) onCloseMobileMenu?.();
    } else if (item.path) {
      // Force-refresh history data when navigating to History
      if (item.path === '/history' || item.id === 'History') {
        try {
          // Invalidate and proactively refetch chats for namespaces used in HistoryPage
          ['frontend', 'models-gallery'].forEach(ns => {
            queryClient.invalidateQueries({ queryKey: storeKeys.chats(ns) });
            queryClient.refetchQueries({ queryKey: storeKeys.chats(ns) });
          });
        } catch (e) {
          // Non-fatal
          console.warn('Failed to trigger history refetch:', e);
        }
      }
      navigate(item.path);
      if (isMobile) onCloseMobileMenu?.();
    }
  };

  const handleChildClick = (child: {
    id: string;
    text: string;
    path?: string;
  }) => {
    if (child.path) {
      navigate(child.path);
      if (isMobile) onCloseMobileMenu?.();
    }
  };

  if (!isVisible) return null;

  const effectiveCollapsed = isMobile ? false : isCollapsed;

  const getSidebarStyles = (): React.CSSProperties => {
    const base = {
      height: '100vh',
      background: 'var(--surface-surface_0, white)',
      borderRight: '1px var(--outline-base_em, #F4F4F6) solid',
      flexDirection: 'column' as const,
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      display: 'flex',
      position: 'fixed' as const,
      left: 0,
      top: 0,
      overflow: 'hidden',
      ...style,
    };
    if (isMobile) {
      return {
        ...base,
        width: 304,
        zIndex: 1001,
        transition: 'transform 0.3s ease',
        transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
      };
    }
    return {
      ...base,
      width: isCollapsed ? 62 : 304,
      zIndex: 1000,
      transition: 'width 0.3s ease',
    };
  };

  const sidebarPanel = (
    <div style={getSidebarStyles()} className={className}>
      {/* Top Section */}
      <div
        style={{
          alignSelf: 'stretch',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          display: 'flex',
        }}
      >
        {/* Header */}
        <div
          style={{
            alignSelf: 'stretch',
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 8,
            paddingBottom: 8,
            justifyContent: 'space-between',
            alignItems: 'center',
            display: 'flex',
            position: 'relative',
          }}
        >
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
          >
            <h1
              style={{
                margin: 0,
                color: 'var(--text-primary_med_em, #5E64FF)',
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: 36,
                lineHeight: '48px',
                letterSpacing: '-0.01em',
                opacity: effectiveCollapsed ? 0 : 1,
                transform: effectiveCollapsed ? 'translateX(-20px)' : 'translateX(0)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                whiteSpace: 'nowrap',
              }}
            >
              swarmind
            </h1>
          </div>
          {isMobile ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onCloseMobileMenu}
              style={{
                width: 40,
                height: 40,
                padding: 6,
                flexShrink: 0,
                minWidth: 36,
              }}
              aria-label="Close menu"
            >
              <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 400 }}>×</span>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={onToggle}
              style={{
                width: 40,
                height: 40,
                padding: 6,
                flexShrink: 0,
                minWidth: 36,
              }}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <img
                src="/icons/double_arrows.svg"
                alt=""
                style={{
                  width: 26,
                  height: 26,
                  transform: isCollapsed
                    ? 'rotate(180deg) translateY(-2px)'
                    : 'translateY(2px)',
                  transition: 'transform 0.3s ease',
                }}
              />
            </Button>
          )}
        </div>

        {/* Sections */}
        {!effectiveCollapsed && (
          <div
            style={{
              alignSelf: 'stretch',
              paddingLeft: 12,
              paddingRight: 12,
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              display: 'flex',
            }}
          >
            {/* General Section */}
            <div
              style={{
                alignSelf: 'stretch',
                paddingTop: 24,
                paddingLeft: 16,
                paddingRight: 16,
                justifyContent: 'flex-start',
                alignItems: 'flex-end',
                gap: 8,
                display: 'flex',
              }}
            >
              <div
                style={{
                  color: 'var(--text-low_em, #8C929C)',
                  fontSize: 11,
                  fontFamily: 'Inter',
                  fontWeight: '500',
                  lineHeight: '16px',
                  wordWrap: 'break-word',
                }}
              >
                GENERAL
              </div>
            </div>

            {/* Navigation Items */}
            <div
              style={{
                alignSelf: 'stretch',
                paddingTop: 8,
                paddingBottom: 8,
                borderRadius: 12,
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                display: 'flex',
              }}
            >
              {navigationItems.map(item => (
                <React.Fragment key={item.id}>
                  <SidebarItem
                    iconType={item.iconType}
                    text={item.text}
                    isActive={activeItem === item.id}
                    hasSubmenu={item.hasSubmenu}
                    isCollapsed={effectiveCollapsed}
                    onClick={() => handleItemClick(item)}
                    isOpen={openSubmenus.has(item.id)}
                  />
                  {/* render children */}
                  {!effectiveCollapsed &&
                    item.children &&
                    openSubmenus.has(item.id) && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: 'calc(100% - 22px)',
                          marginLeft: 3,
                        }}
                      >
                        {/* Arrow column */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            width: 40,
                            margin: 0,
                            padding: 0,
                          }}
                        >
                          {item.children.map((child, idx) => (
                            <img
                              className="svgImg"
                              key={child.id + '-arrow'}
                              src={
                                idx === (item.children?.length || 0) - 1
                                  ? '/icons/slim_arrows/arrow_submenu_L.svg'
                                  : '/icons/slim_arrows/arrow_submenu.svg'
                              }
                              alt="submenu-arrow"
                              style={{
                                width: 40,
                                height: 40,
                                display: 'block',
                                margin: 0,
                                padding: 0,
                                boxSizing: 'border-box',
                              }}
                            />
                          ))}
                        </div>
                        {/* Submenu items */}
                        <div style={{ flex: 1, marginRight: -19 }}>
                          {item.children.map(child => (
                            <SidebarItem
                              key={child.id}
                              iconType="none"
                              text={child.text}
                              isActive={activeItem === child.id}
                              isCollapsed={false}
                              showIcon={false}
                              indent={0.1}
                              onClick={() => handleChildClick(child)}
                              isOpen={openSubmenus.has(item.id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {effectiveCollapsed && (
          <div
            style={{
              width: '100%',
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: 8,
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              gap: 0,
            }}
          >
            {navigationItems.map(item => (
              <SidebarItem
                key={item.id}
                iconType={item.iconType as IconProps['iconType']}
                text={item.text}
                isActive={activeItem === item.id}
                hasSubmenu={item.hasSubmenu}
                isCollapsed={effectiveCollapsed}
                onClick={() => handleItemClick(item)}
                isOpen={openSubmenus.has(item.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom Section */}
      <div
        style={{
          alignSelf: 'stretch',
          paddingLeft: 12,
          paddingRight: 12,
          paddingTop: 16,
          paddingBottom: 16,
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 12,
          display: 'flex',
        }}
      >
        {/* CTA Items */}
        <div
          style={{
            alignSelf: 'stretch',
            borderRadius: 12,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            gap: 2,
          }}
        >
          {ctaItems.map(item => (
            <SidebarItem
              key={item.id}
              iconType={item.iconType}
              text={item.text}
              isActive={activeItem === item.id}
              isCollapsed={effectiveCollapsed}
              onClick={() => handleItemClick(item)}
            />
          ))}
        </div>

        {/* Divider */}
        <div
          style={{
            alignSelf: 'stretch',
            height: 1,
            background: 'var(--outline-base_em, #F4F4F6)',
          }}
        />

        {/* Social Icons (hidden on mobile) & Theme Switcher */}
        {!isCollapsed && (
          <>
            {!isMobile && (
              <div
                style={{
                  alignSelf: 'stretch',
                  paddingLeft: 4,
                  paddingRight: 4,
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  display: 'flex',
                }}
              >
                {['telegram', 'github', 'x'].map(icon => (
                  <Button
                    key={icon}
                    variant="ghost"
                    size="sm"
                    style={{
                      padding: 8,
                      width: 'auto',
                      height: 'auto',
                      minWidth: 'auto',
                    }}
                    aria-label={`Visit ${icon}`}
                  >
                    <img
                      src={`/icons/${icon}.svg`}
                      alt=""
                      style={{ width: 22, height: 22 }}
                    />
                  </Button>
                ))}
              </div>
            )}

            <ThemeSwitcher />
          </>
        )}

        {isCollapsed && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['telegram', 'github', 'x'].map(icon => (
                <Button
                  key={icon}
                  variant="ghost"
                  size="sm"
                  style={{
                    width: 40,
                    height: 40,
                    padding: 8,
                    minWidth: 'auto',
                  }}
                  aria-label={`Visit ${icon}`}
                >
                  <img
                    src={`/icons/${icon}.svg`}
                    alt=""
                    style={{ width: 22, height: 22 }}
                  />
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isMobileMenuOpen && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Close menu"
            onClick={onCloseMobileMenu}
            onKeyDown={e => e.key === 'Enter' && onCloseMobileMenu?.()}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              background: 'rgba(0,0,0,0.4)',
              cursor: 'pointer',
            }}
          />
        )}
        {sidebarPanel}
      </>
    );
  }
  return sidebarPanel;
};

Sidebar.displayName = 'Sidebar';

export default Sidebar;
